// src/components/EditAssessmentScheduleModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Toast from "../components/Toast";

// Import slice actions
import { getAllUsers } from "../redux/slices/userSlice";
import { getMyFacilities } from "../redux/slices/facilitySlice";
import { getCategories } from "../redux/slices/formSlice";

const EditAssessmentScheduleModal = ({
  scheduleData,
  isEditMode = false,
  onClose,
  onSave,
  isSaving = false,
  facilities = [],
  isLoadingFacilities = false,
}) => {
  const dispatch = useDispatch();

  // Get the entire state first
  const fullState = useSelector((state) => state);

  // Destructure with fallbacks
  const userState = fullState.user || {};
  const formState = fullState.form || {};

  const { usersList: { data: users = [] } = {}, isLoadingUsers = false } =
    userState;

  const { categories = [], isLoading: isLoadingCategories = false } = formState;

  // Local form states
  const [selectedFacility, setSelectedFacility] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleType, setScheduleType] = useState("week");
  const [offset, setOffset] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (isEditMode && scheduleData) {
      setSelectedFacility(scheduleData.facility_id?.toString() || "");
      setSelectedUser(scheduleData.user_id?.toString() || "");
      setSelectedCategory(scheduleData.category_id?.toString() || "");
      setScheduleName(scheduleData.name || "");
      setScheduleType(scheduleData.type || "week");
      setOffset(scheduleData.offset?.toString() || "");
    }
  }, [isEditMode, scheduleData]);

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  // Fetch users when facility is selected
  useEffect(() => {
    if (selectedFacility) {
      setSelectedUser("");
      dispatch(
        getAllUsers({
          page: 1,
          perPage: 100,
          facility_id: selectedFacility,
        }),
      );
    } else {
      setSelectedUser("");
    }
  }, [selectedFacility, dispatch]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - only required fields
    if (!selectedFacility || !selectedUser || !selectedCategory) {
      showToast("Please fill in all required fields", "error");
      return;
    }

    // Validate offset only if provided
    if (offset !== "" && (parseInt(offset) < 1 || isNaN(parseInt(offset)))) {
      showToast("Offset must be a positive number", "error");
      return;
    }

    // Prepare the update data
    const updateData = {
      category_id: parseInt(selectedCategory),
      facility_id: parseInt(selectedFacility),
      user_id: parseInt(selectedUser),
    };

    // Add ID if in edit mode
    if (isEditMode && scheduleData?.id) {
      updateData.id = scheduleData.id;
    }

    // Only add schedule fields if they are provided
    if (scheduleName && scheduleName.trim() !== "") {
      updateData.name = scheduleName;
    }

    if (scheduleType) {
      updateData.type = scheduleType;
    }

    if (offset !== "" && offset !== null) {
      updateData.offset = parseInt(offset);
    }

    // Call the onSave callback with the update data
    onSave(updateData);
  };

  // Reset form when modal closes
  const handleClose = () => {
    if (!isEditMode) {
      setSelectedFacility("");
      setSelectedUser("");
      setSelectedCategory("");
      setScheduleName("");
      setScheduleType("week");
      setOffset("");
    }
    if (onClose) onClose();
  };

  // If any required slice is missing, show a warning
  if (!fullState.user || !fullState.form) {
    return (
      <div className="announcementModal assignedFacilityModal">
        <div style={{ padding: "20px", textAlign: "center", color: "#D32F2F" }}>
          <h4>Missing Redux Slices</h4>
          <p>Please add the following reducers to your store:</p>
          <ul style={{ textAlign: "left", display: "inline-block" }}>
            {!fullState.user && <li>userReducer</li>}
            {!fullState.form && <li>formReducer</li>}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="announcementModal assignedFacilityModal">
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "" })}
      />

      <form onSubmit={handleSubmit}>
        <div className="row position-relative">
          {/* Facility Selection */}
          <div className="col-md-12">
            <div className="form-group mb-2.5">
              <label htmlFor="facilitySelect" className="fw-bold mb-2">
                Facility *
              </label>
              <select
                id="facilitySelect"
                className="form-control"
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                disabled={isSaving || isLoadingFacilities}
                required>
                <option value="">Select Facility</option>
                {isLoadingFacilities ? (
                  <option disabled>Loading facilities...</option>
                ) : facilities && facilities.length > 0 ? (
                  facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No facilities found</option>
                )}
              </select>
            </div>
          </div>

          {/* User Selection */}
          <div className="col-md-12">
            <div className="form-group mb-2.5">
              <label htmlFor="userSelect" className="fw-bold mb-2">
                Assign User *
              </label>
              <select
                id="userSelect"
                className="form-control"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={isSaving || isLoadingUsers || !selectedFacility}
                required>
                <option value="">
                  {!selectedFacility
                    ? "Select a facility first"
                    : "Select User"}
                </option>
                {isLoadingUsers && selectedFacility ? (
                  <option disabled>Loading users for this facility...</option>
                ) : users && users.length > 0 && selectedFacility ? (
                  users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.role || "User"}
                      {user.email ? ` (${user.email})` : ""}
                    </option>
                  ))
                ) : selectedFacility && !isLoadingUsers ? (
                  <option disabled>No users found for this facility</option>
                ) : null}
              </select>
            </div>
          </div>

          {/* Category Selection */}
          <div className="col-md-12">
            <div className="form-group mb-2.5">
              <label htmlFor="categorySelect" className="fw-bold mb-2">
                Category *
              </label>
              <select
                id="categorySelect"
                className="form-control"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={isSaving || isLoadingCategories}
                required>
                <option value="">Select Category</option>
                {isLoadingCategories ? (
                  <option disabled>Loading categories...</option>
                ) : categories && categories.length > 0 ? (
                  categories.map((category) => (
                    <option
                      key={category.category_id}
                      value={category.category_id}>
                      {category.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No categories found</option>
                )}
              </select>
            </div>
          </div>

          {/* Schedule Name - Optional */}
          <div className="col-md-12">
            <div className="form-group mb-2.5">
              <label htmlFor="scheduleName" className="fw-bold mb-2">
                Schedule Name (Optional)
              </label>
              <input
                type="text"
                id="scheduleName"
                className="form-control"
                value={scheduleName}
                onChange={(e) => setScheduleName(e.target.value)}
                placeholder="Enter schedule name"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Schedule Type and Offset - Both Optional */}
          <div className="col-md-6">
            <div className="form-group mb-2.5">
              <label htmlFor="scheduleType" className="fw-bold mb-2">
                Type (Optional)
              </label>
              <select
                id="scheduleType"
                className="form-control"
                value={scheduleType}
                onChange={(e) => setScheduleType(e.target.value)}
                disabled={isSaving}>
                <option value="week">Week</option>
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="offset" className="fw-bold mb-2">
                Offset (Optional)
              </label>
              <input
                type="number"
                id="offset"
                className="form-control"
                value={offset}
                onChange={(e) => setOffset(e.target.value)}
                min="1"
                step="1"
                placeholder="Enter offset"
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="col-md-12 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary px-3 py-2 fs-6"
              onClick={handleClose}
              disabled={isSaving}
              style={{ marginRight: "10px" }}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn announceButton px-3 py-2 fs-6"
              disabled={
                isSaving ||
                !selectedFacility ||
                !selectedUser ||
                !selectedCategory
              }>
              {isSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"></span>
                  {isEditMode ? "Updating..." : "Saving..."}
                </>
              ) : isEditMode ? (
                "Update Schedule"
              ) : (
                "Save Schedule"
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditAssessmentScheduleModal;
